import { useTranslation } from 'react-i18next';
import { MessageSquare, Pencil, Plus, Trash2 } from 'lucide-react';
import type { MiniSiteReview } from '@worki/shared';
import { Button, EmptyState } from '../ui.js';
import { currentLocale } from '../../i18n.js';
import { formatRelative } from '../../lib/format.js';
import { outOfTen, reviewScore } from './score.js';

/**
 * Section 4 of the gallery page. The list scrolls in a box of its own, so
 * forty reviews do not push the contact bar a screen away — the visitor reads
 * as many as they like and the page stays where it was.
 *
 * The edit controls exist only when their handlers are passed: the public page
 * passes none, and a review there is something to read, not to change.
 */
export function ReviewList({
  reviews,
  onAdd,
  onEdit,
  onDelete,
}: {
  reviews: MiniSiteReview[];
  onAdd?: () => void;
  onEdit?: (review: MiniSiteReview) => void;
  onDelete?: (review: MiniSiteReview) => void;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();
  return (
    <section className="space-y-3" aria-labelledby="ms-reviews-heading" id="reviews">
      <div className="flex items-center justify-between gap-2">
        <h2 id="ms-reviews-heading" className="text-base font-bold">
          {t('miniSite.reviews')} <span className="text-muted-foreground ltr-nums">({reviews.length})</span>
        </h2>
        {onAdd && (
          <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={onAdd}>
            {t('miniSite.newRating')}
          </Button>
        )}
      </div>

      {reviews.length === 0 ? (
        <EmptyState icon={<MessageSquare className="h-6 w-6 mx-auto" />} title={t('miniSite.noReviews')} />
      ) : (
        <div className="max-h-[420px] overflow-y-auto overscroll-contain space-y-3 pe-1" tabIndex={0}>
          {reviews.map((review) => {
            const score = reviewScore(review);
            return (
              <article key={review.id} className="glass-card list-tint rounded-xl p-3 space-y-2">
                <div className="flex items-start gap-3">
                  <span className="h-10 w-10 rounded-full bg-gradient-to-br from-primary/20 to-info/20 text-sm font-bold flex items-center justify-center shrink-0">
                    {review.customerName.trim().charAt(0)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold truncate">{review.customerName}</h3>
                    <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground flex-wrap">
                      {review.city && <span>{review.city}</span>}
                      {review.showPhone && review.phone && (
                        <>
                          {review.city && <span aria-hidden>·</span>}
                          <span dir="ltr" className="ltr-nums">
                            {review.phone}
                          </span>
                        </>
                      )}
                      <span aria-hidden>·</span>
                      <span>{formatRelative(review.createdAt, locale)}</span>
                    </div>
                  </div>
                  {score !== null && (
                    <span className="h-9 min-w-9 px-2 rounded-lg bg-primary/10 text-primary text-sm font-bold flex items-center justify-center ltr-nums shrink-0">
                      {outOfTen(score)}
                    </span>
                  )}
                  {onEdit && (
                    <button
                      type="button"
                      aria-label={t('common.edit')}
                      onClick={() => onEdit(review)}
                      className="h-8 w-8 rounded-lg text-muted-foreground hover:bg-muted flex items-center justify-center"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                  {onDelete && (
                    <button
                      type="button"
                      aria-label={t('common.delete')}
                      onClick={() => onDelete(review)}
                      className="h-8 w-8 rounded-lg text-destructive hover:bg-destructive/10 flex items-center justify-center"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <p className="text-sm text-muted-foreground whitespace-pre-line">{review.body}</p>
              </article>
            );
          })}
        </div>
      )}

      <hr className="border-border/60" />
    </section>
  );
}
